import React from "react";
import { useEffect } from 'react';
// react library for routing
import { useNavigate } from "react-router-dom";
import OrgPanel from "./OrgPanel/OrgPanel";
import { useAuth } from "react-oidc-context";
import { useMatch, useLocation } from "react-router";
import { setAuthHeader } from "../utilities/axiosHelper";
import { getUserDefaultOrg, getOrganization, getUserRoles, OnLogin, getAllTenantsUserIsUnder } from "../utilities/orgPanelApiCalls";
import {
    useStore,
    setCurrentOrganizationId,
    setCurrentOrganization,
    setIsLoading,
    setUserRoles,
    setTenantCollectionAndUserAccount,
    getSelectedTenant,
    changeOrganizationAndLoad,
    unsetTenantCollectionAndUserAccount
} from "../utilities/appStore";
import { UrlToStateMapper } from "../utilities/UrlToStateMapper";
import { ApplicationUserState } from "../components/Enums/ApplicationUserState";
import { isLandingPage, isRequiresAuthLandingPage } from "../constants/landingPageList";

const getStateFromSearch = ({ search }) => {
    const params = new URLSearchParams(search);
    return params.get("state");
}
function Layout() {
    const auth = useAuth();
    const navigate = useNavigate();
    var urlData = useMatch("/Organizations/:orgId/:subRoute");
    var urlSubRoute = urlData?.params?.subRoute ?? "Overview";
    var route = useLocation();
    let oidcState = getStateFromSearch(route);

    let currentOrganizationId = useStore((state) => state.CurrentOrganizationId);
    let currentOrg = useStore((state) => state.CurrentOrganization);

    useEffect(() => { //Looks for logout_redirect_url
        if (oidcState) {
            const safeParseJSON = (jsonString, defaultValue = null) => {
                try {
                    return JSON.parse(jsonString);
                } catch (error) {
                    console.error("Invalid JSON:", error);
                    return defaultValue;
                }
            };
            let stateObj = safeParseJSON(localStorage.getItem(`oidc.${oidcState}`));
            let redirectUrl = stateObj?.data?.postLogoutRedirect;
            if (redirectUrl) {
                navigate(redirectUrl);
            }
        }
    }, [oidcState, navigate])

    useEffect(() => {
        if (auth?.user?.access_token) {
            setAuthHeader(auth.user.access_token);
        }


    }, [auth])
    useEffect(() => {
        async function handleLogin() {
            setAuthHeader(auth.user?.access_token);
            if (auth.user?.access_token) {
                OnLogin();
            }
        } handleLogin();
    }, [auth.user])


    async function DetermineApplicationUserState() {
        var userState = 0;

    // Load the Default Org, or update user state enum accordingly if none exists.
    async function verifyDefaultOrg() {
        setIsLoading(true);
        let tenantResponse = await getAllTenantsUserIsUnder();
        
        if (!tenantResponse || tenantResponse?.assignedTenants.length === 0) {
            // Couldn't get the user's default organization. They must not have one
            unsetTenantCollectionAndUserAccount(); //resetting state and removing cookies
            userState |= ApplicationUserState.UserHasNoOrganization;
            setIsLoading(false);
            return;
        }
        else {
            setTenantCollectionAndUserAccount(tenantResponse);
            let selectedTenent = getSelectedTenant();
            if (selectedTenent) {
                changeOrganizationAndLoad(selectedTenent.accountId);
            }
        }
        setIsLoading(false);
    }

        
        // Is the user token is valid?
        if (auth?.user?.access_token && Date.now() < auth.user.expires_at * 1000) {
            userState |= ApplicationUserState.UserIsAuthenticated;
        }

        // Is user trying to load a landing page?
        if (isLandingPage(route?.pathname?.toLowerCase() ?? "") && !isRequiresAuthLandingPage(route?.pathname?.toLowerCase() ?? "")) {
            userState |= ApplicationUserState.UserIsLoadingLandingPage;
            return userState;
        }


        if (!auth.isAuthenticated)
            return userState;

        // Is user trying to load a landing page?
        if (isRequiresAuthLandingPage(route?.pathname?.toLowerCase() ?? "")) {
            userState |= ApplicationUserState.UserIsLoadingLandingPageRequireAuth;
            return userState;
        }

        // Is this a valid org?
        if (currentOrganizationId > 0 && !isNaN(currentOrganizationId)) {

            if (currentOrganizationId === currentOrg.accountId) {

                // We've already loaded this org and have set our currentOrganizationId to be here
                userState |= ApplicationUserState.UserOrgIsLoaded;
                return userState;
            }

            // User newly arrived at an organization, check the user's access to it
            let response;
            setIsLoading(true);
            response = await getOrganization(currentOrganizationId);

            if (response === 403 || response === 401 || response === 404) {
                // Not authorized to view this organization, or it doesn't exist. Are they allowed to view another one?
                await verifyDefaultOrg();
                return userState;
            }
            else {
                // User has access to this org and Org is now loaded.
                setCurrentOrganization(response.data);
                userState |= ApplicationUserState.UserOrgIsLoaded;
                setIsLoading(false);
                return userState;
            }
        }
        else {
            //User hasn't selected an org - attempt to load the default
            await verifyDefaultOrg();
            return userState;
        }
    }


    useEffect(() => {
        async function loadRoles() {
            if (auth.isLoading || !auth.isAuthenticated)
                return;
            var response = await getUserRoles();
            setUserRoles(response.data);
        } loadRoles();

    }, [auth]);

    useEffect(() => {
        async function loadUserData() {

            if (auth.isLoading)
                return;

            //Auth is loaded, we're ready to navigate the user.
            var result = await DetermineApplicationUserState();

            // If user is loading a landing page - don't renavigate
            if ((result & ApplicationUserState.UserIsLoadingLandingPage) === ApplicationUserState.UserIsLoadingLandingPage) {
                return;
            }
            const isLoadingAuthLandingPage = (result & ApplicationUserState.UserIsLoadingAuthLandingPageAlreadyLoggedIn) === ApplicationUserState.UserIsLoadingAuthLandingPageAlreadyLoggedIn;
            // If autologin was attempted and failed, redirect to login.
            if (!auth.isAuthenticated && auth?.error?.error === "login_required") {

                let authParams = {}
                if (isLoadingAuthLandingPage) {
                    authParams.redirect_uri = window.location.href;
                }

                await auth.signinRedirect(authParams);
            }

            // If user is loading an auth landing page - don't renavigate
            if (isLoadingAuthLandingPage) {
                return;
            }

            

            // If user is loading an auth landing page - don't renavigate
            if ((result & ApplicationUserState.UserIsLoadingLandingPageRequireAuth) === ApplicationUserState.UserIsLoadingLandingPageRequireAuth) {
                return;
            }

            // If user is authenticated
            if ((result & ApplicationUserState.UserIsAuthenticated) === ApplicationUserState.UserIsAuthenticated) {

                //If Org is loaded
                if ((result & ApplicationUserState.UserOrgIsLoaded) === ApplicationUserState.UserOrgIsLoaded) {
                    //Close, doesn't yet account for the "Processing" Page
                    navigate(`/Organizations/${currentOrganizationId}/${urlSubRoute}`);
                    return;
                }
                // If User has no organization
                if ((result & ApplicationUserState.UserHasNoOrganization) === ApplicationUserState.UserHasNoOrganization) {
                    navigate("/notauthorized");
                    return;
                }
            }
            else {
                navigate("/");
                return;

            }

        } loadUserData();
    }, [currentOrganizationId, currentOrg, auth]); //minor issue, will navigate on Auth refresh. Euugh.

    return (
        <>
            <UrlToStateMapper />
            <OrgPanel />
        </>
    );
}

export default Layout;